import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Receipt, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useBranch } from '../contexts/BranchContext';
import { branchApiService, Branch } from '../services/branchApi';
import { customerApiService, Customer } from '../services/customerApi';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { hsnApiService, HsnCode } from '../services/hsnApi';
import { taxRateHistoryApiService } from '../services/taxRateHistoryApi';
import {
  creditDebitNoteApiService,
  CompleteCreditDebitNoteCreateRequest,
  NoteType,
} from '../services/creditDebitNoteApi';
import { grandTotalTaxFromLines, roundLineTaxAmounts, roundMoney2 } from '../utils/taxUtils';

function toEffectiveRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function amountInWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };

  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';

  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);

  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

const REASON_OPTIONS = [
  { value: 'NOT_APPLICABLE', label: 'Not Applicable', payload: 'Not Applicable' },
  { value: '01_SALES_RETURN', label: '01-Sales Return', payload: '01-Sales Return' },
  { value: '02_POST_SALE_DISCOUNT', label: '02-Post Sale Discount', payload: '02-Post Sale Discount' },
  { value: '03_DEFICIENCY_IN_SERVICES', label: '03-Deficiency in services', payload: '03-Deficiency in services' },
  { value: '04_CORRECTION_IN_INVOICE', label: '04-Correction in Invoice', payload: '04-Correction in Invoice' },
  { value: '05_CHANGE_IN_POS', label: '05-Change in POS', payload: '05-Change in POS' },
  { value: '06_FINALIZATION_OF_PROVISIONAL_ASSESSMENT', label: '06-Finalization of Provisional assessment', payload: '06-Finalization of Provisional assessment' },
  { value: 'OTHERS', label: '07-Others', payload: 'OTHERS' },
] as const;

type ReasonOptionValue = (typeof REASON_OPTIONS)[number]['value'];

type LineMode = 'product' | 'manual';

type ManualLineRow = {
  key: string;
  lineMode: LineMode;
  endgoodId: number | null;
  itemDescription: string;
  hsn: string;
  uom: string;
  qty: number;
  rate: number;
  amount: number;
  cgst: number;
  sgst: number;
  igst: number;
};

function lineGstAmountsForApi(l: ManualLineRow): { cgst: number; sgst: number; igst: number } {
  const base = l.amount;
  return roundLineTaxAmounts({
    cgst: (base * toEffectiveRate(l.cgst)) / 100,
    sgst: (base * toEffectiveRate(l.sgst)) / 100,
    igst: (base * toEffectiveRate(l.igst)) / 100,
  });
}

function newEmptyLine(lineMode: LineMode = 'product'): ManualLineRow {
  return {
    key: String(Date.now()) + Math.random(),
    lineMode,
    endgoodId: null,
    itemDescription: '',
    hsn: '',
    uom: 'Nos',
    qty: 0,
    rate: 0,
    amount: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
  };
}

export function ManualCreditDebitNoteFormPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const [noteType, setNoteType] = useState<NoteType>('CREDIT_NOTE');
  const [noteDate, setNoteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [hsnTaxRateDate, setHsnTaxRateDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [billToId, setBillToId] = useState('');
  const [shipToId, setShipToId] = useState('');
  const [reason, setReason] = useState<ReasonOptionValue>('NOT_APPLICABLE');
  const [otherReason, setOtherReason] = useState('');
  const [lines, setLines] = useState<ManualLineRow[]>([newEmptyLine('product')]);
  const [qtyDrafts, setQtyDrafts] = useState<Record<string, string>>({});
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({});
  const [amountDrafts, setAmountDrafts] = useState<Record<string, string>>({});

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [endgoods, setEndgoods] = useState<Endgood[]>([]);
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [branchDetails, setBranchDetails] = useState<Branch | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingData(true);
    void (async () => {
      try {
        const [customersRes, endgoodsRes, hsnList] = await Promise.all([
          customerApiService.getCustomers(1, 500),
          endgoodApiService.getEndgoodsIdName(),
          hsnApiService.getHsnCodes(),
        ]);
        if (cancelled) return;
        setCustomers(customersRes?.success && customersRes.data ? customersRes.data : []);
        setEndgoods(endgoodsRes?.success && endgoodsRes.data ? endgoodsRes.data : []);
        setHsnCodes(Array.isArray(hsnList) ? hsnList : []);
      } catch {
        if (!cancelled) toast.error('Failed to load form data');
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Default ship-to = bill-to for manual notes (user can change ship-to if needed).
  useEffect(() => {
    if (!billToId) return;
    if (!shipToId) {
      setShipToId(billToId);
      return;
    }
  }, [billToId, shipToId]);

  useEffect(() => {
    if (selectedBranch == null) {
      setBranchDetails(null);
      return;
    }
    branchApiService.getBranchById(String(selectedBranch)).then((b) => setBranchDetails(b ?? null)).catch(() => setBranchDetails(null));
  }, [selectedBranch]);

  const billToCustomer = useMemo(
    () => customers.find((c) => String(c.id) === billToId) ?? null,
    [customers, billToId]
  );

  const sameState = useMemo(() => {
    if (!branchDetails || !billToCustomer) return true;
    return (branchDetails.stateCode ?? '') === (billToCustomer.stateCode ?? '');
  }, [branchDetails, billToCustomer]);

  const applyTaxRatesToLine = useCallback(
    async (line: ManualLineRow, hsnCode: string, taxDate?: string): Promise<ManualLineRow> => {
      const hsnRecord = hsnCode ? hsnCodes.find((h) => (h.hsnCode ?? '') === hsnCode) : null;
      if (!hsnRecord) return { ...line, cgst: 0, sgst: 0, igst: 0 };
      const dateForApi = (taxDate ?? hsnTaxRateDate).split('T')[0] || hsnTaxRateDate;
      let cgstPct = 0;
      let sgstPct = 0;
      let igstPct = 0;
      try {
        const applicable = await taxRateHistoryApiService.getApplicableTaxRate(hsnRecord.id, dateForApi);
        if (sameState) {
          cgstPct = applicable.cgstRate ?? 0;
          sgstPct = applicable.sgstRate ?? 0;
        } else {
          igstPct = applicable.igstRate ?? 0;
        }
      } catch {
        if (sameState) {
          cgstPct = hsnRecord.currentCgstRate ?? 0;
          sgstPct = hsnRecord.currentSgstRate ?? 0;
        } else {
          igstPct = hsnRecord.currentIgstRate ?? 0;
        }
      }
      return { ...line, cgst: cgstPct, sgst: sgstPct, igst: igstPct };
    },
    [hsnCodes, hsnTaxRateDate, sameState]
  );

  const handleHsnTaxRateDateChange = useCallback(
    (value: string) => {
      setHsnTaxRateDate(value);
      const dateForApi = value.split('T')[0] || value;
      setLines((prev) => {
        void (async () => {
          const next = await Promise.all(
            prev.map(async (row) => {
              if (!row.hsn?.trim()) return row;
              return applyTaxRatesToLine(row, row.hsn, dateForApi);
            })
          );
          setLines(next);
        })();
        return prev;
      });
    },
    [applyTaxRatesToLine]
  );

  const handleEndgoodChange = async (lineKey: string, endgoodIdStr: string) => {
    const endgoodId = endgoodIdStr ? parseInt(endgoodIdStr, 10) : null;
    const eg = endgoods.find((e) => e.id === endgoodId);
    let hsnFromGood = eg?.hsn?.hsnCode ?? '';
    if (endgoodId != null && !hsnFromGood) {
      try {
        const egDetail = await endgoodApiService.getEndgoodById(String(endgoodId));
        hsnFromGood = egDetail?.hsn?.hsnCode ?? '';
      } catch {
        hsnFromGood = '';
      }
    }
    const baseRow: ManualLineRow = {
      ...(lines.find((l) => l.key === lineKey) ?? newEmptyLine('product')),
      key: lineKey,
      lineMode: 'product',
      endgoodId,
      itemDescription: '',
      hsn: hsnFromGood,
    };
    setLines((prev) => prev.map((row) => (row.key === lineKey ? baseRow : row)));
    if (hsnFromGood) {
      const updated = await applyTaxRatesToLine(baseRow, hsnFromGood);
      setLines((prev) => prev.map((r) => (r.key === lineKey ? updated : r)));
    }
  };

  const setLineMode = (lineKey: string, lineMode: LineMode) => {
    setLines((prev) =>
      prev.map((row) => {
        if (row.key !== lineKey) return row;
        if (lineMode === 'manual') {
          return {
            ...row,
            lineMode,
            endgoodId: null,
            itemDescription: row.itemDescription || '',
            hsn: row.hsn,
          };
        }
        return {
          ...row,
          lineMode,
          itemDescription: '',
          endgoodId: row.endgoodId,
        };
      })
    );
  };

  const handleLineHsnChange = async (lineKey: string, hsn: string) => {
    setLines((prev) => prev.map((r) => (r.key === lineKey ? { ...r, hsn } : r)));
    const row = lines.find((l) => l.key === lineKey);
    if (row) {
      const updated = await applyTaxRatesToLine({ ...row, hsn }, hsn);
      setLines((prev) => prev.map((r) => (r.key === lineKey ? updated : r)));
    }
  };

  const isValidDecimalInput = (value: string) => /^\d*\.?\d*$/.test(value);

  const setLineQty = (lineKey: string, value: number) => {
    setLines((prev) =>
      prev.map((r) => {
        if (r.key !== lineKey) return r;
        const qty = value;
        const amount = qty > 0 ? qty * (r.rate ?? 0) : 0;
        return { ...r, qty, amount };
      })
    );
  };
  const setLineRate = (lineKey: string, value: number) => {
    setLines((prev) =>
      prev.map((r) => {
        if (r.key !== lineKey) return r;
        const rate = value;
        const amount = (r.qty ?? 0) > 0 ? (r.qty ?? 0) * rate : 0;
        return { ...r, rate, amount };
      })
    );
  };

  const setLineAmount = (lineKey: string, value: number) => {
    setLines((prev) =>
      prev.map((r) => {
        if (r.key !== lineKey) return r;
        const amount = value;
        const qty = (r.qty ?? 0) > 0 ? (r.qty ?? 0) : 1;
        const rate = qty > 0 ? amount / qty : amount;
        return { ...r, qty, rate, amount };
      })
    );
  };

  const validLines = lines.filter((l) => {
    if (l.amount <= 0) return false;
    if (l.lineMode === 'manual') {
      return Boolean(l.itemDescription.trim() && l.hsn.trim());
    }
    return l.endgoodId != null;
  });

  const taxableAmount = validLines.reduce((s, l) => s + (l.amount ?? 0), 0);
  const totalCgst = validLines.reduce((s, l) => s + ((l.amount ?? 0) * toEffectiveRate(l.cgst)) / 100, 0);
  const totalSgst = validLines.reduce((s, l) => s + ((l.amount ?? 0) * toEffectiveRate(l.sgst)) / 100, 0);
  const totalIgst = validLines.reduce((s, l) => s + ((l.amount ?? 0) * toEffectiveRate(l.igst)) / 100, 0);
  const grandTotal = taxableAmount + totalCgst + totalSgst + totalIgst;
  const totalTaxAmount = totalCgst + totalSgst + totalIgst;
  const isIgst = totalIgst > 0;

  const handleSave = async () => {
    if (selectedBranch == null) {
      toast.error('Select a branch from the header');
      return;
    }
    if (!billToId) {
      toast.error('Select bill-to customer');
      return;
    }
    if (!shipToId) {
      toast.error('Select ship-to customer');
      return;
    }
    if (validLines.length === 0) {
      toast.error('Add at least one line with qty, rate, and product or manual description + HSN');
      return;
    }
    if (reason === 'OTHERS' && !otherReason.trim()) {
      toast.error('Enter a reason for Others');
      return;
    }

    const resolvedReason =
      reason === 'OTHERS'
        ? `OTHERS: ${otherReason.trim()}`
        : (REASON_OPTIONS.find((option) => option.value === reason)?.payload ?? 'Not Applicable');

    const detailsPayload = validLines.map((l) => {
      const tax = lineGstAmountsForApi(l);
      const amount = l.amount;
      if (l.lineMode === 'manual') {
        return {
          endgoodId: null,
          itemDescription: l.itemDescription.trim(),
          uom: l.uom || 'Nos',
          qty: Math.round(l.qty),
          rate: l.rate,
          hsn: l.hsn,
          cgst: tax.cgst,
          sgst: tax.sgst,
          igst: tax.igst,
          amount,
        };
      }
      return {
        endgoodId: l.endgoodId ?? undefined,
        uom: l.uom || 'Nos',
        qty: Math.round(l.qty),
        rate: l.rate,
        hsn: l.hsn,
        cgst: tax.cgst,
        sgst: tax.sgst,
        igst: tax.igst,
        amount,
      };
    });

    const lineTaxSum = grandTotalTaxFromLines(detailsPayload);
    const grandTotalResolved = roundMoney2(taxableAmount + lineTaxSum);

    const requestData: CompleteCreditDebitNoteCreateRequest = {
      noteType,
      noteDate,
      taxDate: hsnTaxRateDate.split('T')[0] || hsnTaxRateDate,
      branchId: selectedBranch,
      shipTo: parseInt(shipToId, 10),
      billTo: parseInt(billToId, 10),
      reason: resolvedReason,
      totalAmount: taxableAmount,
      grandTotalTax: lineTaxSum,
      grandTotal: grandTotalResolved,
      details: detailsPayload,
    };

    setSaving(true);
    try {
      const response = await creditDebitNoteApiService.createCompleteCreditDebitNote(requestData);
      if (response.success && response.data?.id) {
        navigate(`/invoices/credit-debit-notes/${response.data.id}/view`);
      }
    } catch {
      // toast in service
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate('/invoices/credit-debit-notes')} className="border-primary-200">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Card>
        <CardHeader className="bg-primary-50/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <Receipt className="h-5 w-5" />
            New Manual Credit / Debit Note
          </CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Create a note without linking to an existing invoice number.
          </p>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Note type</Label>
              <Select value={noteType} onValueChange={(v) => setNoteType(v as NoteType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CREDIT_NOTE">Credit Note</SelectItem>
                  <SelectItem value="DEBIT_NOTE">Debit Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Note date</Label>
                  <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Tax rate date</Label>
                  <Input
                    type="date"
                    value={hsnTaxRateDate}
                    onChange={(e) => handleHsnTaxRateDateChange(e.target.value)}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bill to *</Label>
              <Select value={billToId} onValueChange={setBillToId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContentWithSearch searchPlaceholder="Search customer..." debounceMs={300}>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.customerName}
                    </SelectItem>
                  ))}
                </SelectContentWithSearch>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ship to *</Label>
              <Select value={shipToId} onValueChange={setShipToId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContentWithSearch searchPlaceholder="Search customer..." debounceMs={300}>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.customerName}
                    </SelectItem>
                  ))}
                </SelectContentWithSearch>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={(value) => setReason(value as ReasonOptionValue)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reason === 'OTHERS' && (
              <div className="space-y-2">
                <Label>Other reason</Label>
                <Input value={otherReason} onChange={(e) => setOtherReason(e.target.value)} placeholder="Enter reason" />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Line items</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const row = newEmptyLine('product');
                setLines((prev) => [...prev, row]);
                setQtyDrafts((p) => ({ ...p, [row.key]: '' }));
                setRateDrafts((p) => ({ ...p, [row.key]: '' }));
                setAmountDrafts((p) => ({ ...p, [row.key]: '' }));
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add line
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="bg-primary-50">
                <TableHead className="font-semibold text-primary-800 w-36">Line type</TableHead>
                <TableHead className="font-semibold text-primary-800 min-w-[200px]">Product / Description</TableHead>
                <TableHead className="font-semibold text-primary-800 w-28">HSN/SAC</TableHead>
                <TableHead className="font-semibold text-primary-800 w-20">UOM</TableHead>
                <TableHead className="font-semibold text-primary-800 w-24">Qty</TableHead>
                <TableHead className="font-semibold text-primary-800 w-24">Rate</TableHead>
                <TableHead className="font-semibold text-primary-800 w-28">Amount</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>
                    <Select
                      value={row.lineMode}
                      onValueChange={(v) => setLineMode(row.key, v as LineMode)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">From product</SelectItem>
                        <SelectItem value="manual">Manual line</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {row.lineMode === 'manual' ? (
                      <Input
                        value={row.itemDescription}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((r) => (r.key === row.key ? { ...r, itemDescription: e.target.value } : r))
                          )
                        }
                        placeholder="Item description"
                        className="h-8"
                      />
                    ) : (
                      <Select
                        value={row.endgoodId != null ? String(row.endgoodId) : ''}
                        onValueChange={(v) => void handleEndgoodChange(row.key, v)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select product" />
                        </SelectTrigger>
                        <SelectContent>
                          {endgoods.map((eg) => (
                            <SelectItem key={eg.id} value={String(eg.id)}>
                              {eg.endgoodName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.hsn || undefined}
                      onValueChange={(value) => void handleLineHsnChange(row.key, value)}
                      disabled={row.lineMode === 'product' && row.endgoodId != null}
                    >
                      <SelectTrigger className="h-8 min-w-[120px]">
                        <SelectValue placeholder="Select HSN" />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search HSN..." debounceMs={300}>
                        {hsnCodes.map((h) => (
                          <SelectItem key={h.id} value={h.hsnCode}>
                            {h.hsnCode}
                            {h.description ? ` - ${h.description}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContentWithSearch>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.uom}
                      onChange={(e) =>
                        setLines((prev) => prev.map((r) => (r.key === row.key ? { ...r, uom: e.target.value } : r)))
                      }
                      className="h-8 w-20"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={qtyDrafts[row.key] ?? (row.qty === 0 ? '' : String(row.qty))}
                      onChange={(e) => {
                        if (!isValidDecimalInput(e.target.value)) return;
                        setQtyDrafts((p) => ({ ...p, [row.key]: e.target.value }));
                        setLineQty(row.key, e.target.value === '' || e.target.value === '.' ? 0 : parseFloat(e.target.value));
                        setAmountDrafts((p) => {
                          const next = { ...p };
                          delete next[row.key];
                          return next;
                        });
                      }}
                      className="h-8 w-24"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={rateDrafts[row.key] ?? (row.rate === 0 ? '' : String(row.rate))}
                      onChange={(e) => {
                        if (!isValidDecimalInput(e.target.value)) return;
                        setRateDrafts((p) => ({ ...p, [row.key]: e.target.value }));
                        setLineRate(row.key, e.target.value === '' || e.target.value === '.' ? 0 : parseFloat(e.target.value));
                        setAmountDrafts((p) => {
                          const next = { ...p };
                          delete next[row.key];
                          return next;
                        });
                      }}
                      className="h-8 w-24"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={amountDrafts[row.key] ?? (row.amount === 0 ? '' : String(row.amount))}
                      onChange={(e) => {
                        if (!isValidDecimalInput(e.target.value)) return;
                        setAmountDrafts((p) => ({ ...p, [row.key]: e.target.value }));
                        const n = e.target.value === '' || e.target.value === '.' ? 0 : parseFloat(e.target.value);
                        setLineAmount(row.key, n);
                      }}
                      className="h-8 w-28 font-semibold"
                      placeholder="Amount"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600"
                      disabled={lines.length <= 1}
                      onClick={() => {
                        setLines((prev) => prev.filter((r) => r.key !== row.key));
                        setQtyDrafts((p) => {
                          const next = { ...p };
                          delete next[row.key];
                          return next;
                        });
                        setRateDrafts((p) => {
                          const next = { ...p };
                          delete next[row.key];
                          return next;
                        });
                        setAmountDrafts((p) => {
                          const next = { ...p };
                          delete next[row.key];
                          return next;
                        });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {validLines.length > 0 && (
            <Card className="border-2 border-primary-200">
              <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl py-4">
                <CardTitle className="text-lg font-bold text-primary-800">Note Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-lg">
                      <span className="font-medium">Subtotal:</span>
                      <span>₹{taxableAmount.toFixed(2)}</span>
                    </div>
                    {!isIgst ? (
                      <>
                        <div className="flex justify-between text-lg">
                          <span className="font-medium">CGST:</span>
                          <span>₹{totalCgst.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-lg">
                          <span className="font-medium">SGST:</span>
                          <span>₹{totalSgst.toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-lg">
                        <span className="font-medium">IGST:</span>
                        <span>₹{totalIgst.toFixed(2)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between text-2xl font-bold text-primary-800">
                      <span>Grand Total:</span>
                      <span>₹{grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Amount (in words)</Label>
                      <p className="text-sm text-gray-700 border border-gray-200 rounded-xl p-3 bg-gray-50 min-h-[4.5rem]">
                        INR {amountInWords(grandTotal)}
                      </p>
                    </div>
                    <div className="text-sm text-gray-600">
                      Company&apos;s PAN: {branchDetails?.panNo ?? 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Tax: CGST {toDisplayRate(validLines[0]?.cgst ?? 0)}% / SGST {toDisplayRate(validLines[0]?.sgst ?? 0)}% / IGST{' '}
                      {toDisplayRate(validLines[0]?.igst ?? 0)}% (per line HSN)
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/invoices/credit-debit-notes')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || validLines.length === 0}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save and preview
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
